import React, {useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {SendOutlined, VisibilityOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import StatusSituacaoInscricao, {CorSituacaoInscricao} from '../../components/inscricao/StatusSituacaoInscricao.jsx';
import {CampoProcessoSeletivo} from '../../components/cargasaf/CamposCargaSaf.jsx';
import DialogPreviaEmail from '../../dialogs/recursos/DialogPreviaEmail.jsx';
import DialogResultadoEnvioEmails from '../../dialogs/recursos/DialogResultadoEnvioEmails.jsx';
import {
    confirmacaoEnvio,
    formatarDataEnvio,
    formatarNomeComSocial,
    formatarQuantidadeEnvios,
    mensagemEnvioSucesso,
    montarParametrosEnvio,
    montarPayloadEnvio,
    podeEnviar,
    podeVisualizar,
    SEM_REGISTROS_INSCRITOS,
    situacaoDesabilitada
} from '../../components/recursos/envioEmails.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useCreate from '../../../../hooks/useCreate.js';
import useDialogState from '../../../../hooks/useDialogState.js';
import useFetch from '../../../../hooks/useFetch.js';
import {
    URL_ENVIO_EMAILS,
    URL_ENVIO_EMAILS_INSCRICOES,
    URL_ENVIO_EMAILS_MODELO,
    URL_ENVIO_EMAILS_PROCESSOS_SELECAO,
    URL_ENVIO_EMAILS_SITUACOES
} from '../../../../constants/index.js';
import {containerPaper} from '../../../../utils/styles.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const SEM_PREVIA = {assunto: '', corpo: ''};
const SEM_RESULTADO = {enviados: 0, falhas: []};

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    acoesEnvio: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    }
}));

/*
 * Enviar E-mails (módulo RCENVEMAIL): o usuário escolhe o processo de seleção e a situação
 * (só as que têm modelo de e-mail), a tabela mostra os inscritos nessa situação e o botão
 * "Enviar" dispara o e-mail do modelo para os marcados. "Visualizar E-mail" abre a prévia do
 * modelo. Trocar o processo limpa situação, tabela e seleção; falhas do envio abrem o resultado.
 */
export default function PageEnvioEmails() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('RCENVEMAIL');
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const {dialogState, openDialog, closeDialog} = useDialogState({previa: false, resultado: false});
    const [fetchApoio, carregandoApoio] = useFetch({returnInnerLoadingState: true});
    const [fetchInscricoes, carregandoInscricoes] = useFetch({returnInnerLoadingState: true});
    const [fetchModelo, carregandoModelo] = useFetch({returnInnerLoadingState: true});
    const [enviar, enviando] = useCreate({returnInnerLoadingState: true});

    const [processos, setProcessos] = useState([]);
    const [situacoes, setSituacoes] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [situacao, setSituacao] = useState('');
    const [inscricoes, setInscricoes] = useState([]);
    const [selecionados, setSelecionados] = useState([]);
    const [previa, setPrevia] = useState(SEM_PREVIA);
    const [resultado, setResultado] = useState(SEM_RESULTADO);
    // Descarta a resposta de uma listagem já substituída (troca de processo/situação).
    const listagemRef = useRef(0);

    // A credencial renova a cada resposta: os callbacks chamam sempre o fetch do render mais recente.
    const fetchInscricoesRef = useRef(fetchInscricoes);
    fetchInscricoesRef.current = fetchInscricoes;

    useEffect(() => {
        if (podeAcessar) {
            fetchApoio(URL_ENVIO_EMAILS_PROCESSOS_SELECAO, PARAMS_VAZIOS,
                dados => setProcessos(Array.isArray(dados) ? dados : []));
            fetchApoio(URL_ENVIO_EMAILS_SITUACOES, PARAMS_VAZIOS,
                dados => setSituacoes(Array.isArray(dados) ? dados : []));
        }
    }, [podeAcessar]);

    const carregarInscricoes = (processo, codigoSituacao) => {
        const listagem = ++listagemRef.current;
        setInscricoes([]);
        setSelecionados([]);

        fetchInscricoesRef.current(URL_ENVIO_EMAILS_INSCRICOES,
            montarParametrosEnvio({idProcessoSelecao: processo, situacao: codigoSituacao}),
            dados => {
                if (listagem === listagemRef.current) {
                    setInscricoes(Array.isArray(dados) ? dados : []);
                }
            });
    };

    const trocarProcesso = valor => {
        listagemRef.current += 1;
        setIdProcesso(valor);
        setSituacao('');
        setInscricoes([]);
        setSelecionados([]);
    };

    const trocarSituacao = valor => {
        setSituacao(valor);
        carregarInscricoes(idProcesso, valor);
    };

    const visualizar = () => fetchModelo(URL_ENVIO_EMAILS_MODELO,
        montarParametrosEnvio({idProcessoSelecao: idProcesso, situacao}),
        modelo => {
            setPrevia({assunto: modelo?.assunto || '', corpo: modelo?.corpo || ''});
            openDialog('previa')();
        });

    const disparar = () => enviar(URL_ENVIO_EMAILS,
        montarPayloadEnvio({idProcessoSelecao: idProcesso, situacao, selecionados}),
        resposta => {
            const enviados = Number(resposta?.enviados) || 0;
            const falhas = Array.isArray(resposta?.falhas) ? resposta.falhas : [];

            if (enviados > 0 || !falhas.length) {
                createToast({severity: 'success', message: mensagemEnvioSucesso(enviados)});
            }

            if (falhas.length) {
                setResultado({enviados, falhas});
                openDialog('resultado')();
            }

            carregarInscricoes(idProcesso, situacao);
        });

    const confirmarEnvio = () => confirmation(confirmacaoEnvio(selecionados.length), disparar);

    const selectOptions = useMemo(() => ({
        mode: 'multiple',
        selectedRows: selecionados,
        onSelect: (linha, todos) => setSelecionados(Array.isArray(todos) ? todos : [])
    }), [selecionados]);

    const colunas = useMemo(() => [
        {
            label: 'Nome', field: 'nome', filterable: true,
            valueGetter: (valor, row) => formatarNomeComSocial(row.nome, row.nomeSocial)
        },
        {label: 'E-mail', field: 'email', filterable: true, valueGetter: valor => ouTraco(valor)},
        {
            label: 'Situação', field: 'situacao', filterable: false,
            valueGetter: (valor, row) => (row.situacao ? <StatusSituacaoInscricao situacao={row.situacao}/> : ouTraco(''))
        },
        {label: 'Dt. Último Envio', field: 'dataUltimoEnvio', filterable: false, valueGetter: valor => formatarDataEnvio(valor)},
        {label: 'Qtd. de Envios', field: 'quantidadeEnvios', filterable: false, valueGetter: valor => formatarQuantidadeEnvios(valor)}
    ], []);

    // Loading da página pelo LoadingModal global (o BlockUI fica só nas tabelas).
    useCarregamentoPagina(carregandoApoio || carregandoModelo || enviando);

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    const ocupado = carregandoApoio || carregandoInscricoes || carregandoModelo || enviando;

    return (
        <>
            <PageWrapper>
                <PageTitle title='Enviar E-mails'/>
                <Paper className={classes.containerPaper}>
                    <Grid container spacing={3}>
                        <CampoProcessoSeletivo
                            valor={idProcesso}
                            opcoes={processos}
                            onChange={trocarProcesso}
                            label='Processo de Seleção'
                            sm={6}/>
                        <Grid item xs={12} sm={6}>
                            <LabelValue label='Situação *'>
                                <FormControl variant='outlined' fullWidth disabled={!idProcesso}>
                                    <Select
                                        value={situacao}
                                        onChange={evento => trocarSituacao(evento.target.value)}
                                        displayEmpty>
                                        <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                        {situacoes.map(item => (
                                            <MenuItem
                                                key={item.codigo}
                                                value={item.codigo}
                                                disabled={situacaoDesabilitada(item)}>
                                                <CorSituacaoInscricao codigo={item.codigo}/>
                                                {item.descricao}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <OutlinedButton
                                startIcon={<VisibilityOutlined/>}
                                disabled={ocupado || !podeVisualizar({idProcessoSelecao: idProcesso, situacao, situacoes})}
                                onClick={visualizar}>
                                Visualizar E-mail
                            </OutlinedButton>
                        </Grid>
                        <Grid item xs={12}>
                            <BlockUI loading={carregandoInscricoes}>
                                <DataGrid
                                    rows={inscricoes}
                                    comparatorKeys={['id']}
                                    select
                                    selectOptions={selectOptions}
                                    filterOptions={FILTER_OPTIONS}
                                    paginate
                                    paginateOptions={PAGINACAO_PADRAO}
                                    noRecordsText={textoSemRegistros(carregandoInscricoes, SEM_REGISTROS_INSCRITOS)}
                                    columns={colunas}/>
                            </BlockUI>
                        </Grid>
                        <Grid item xs={12} className={classes.acoesEnvio}>
                            <ContainedButton
                                startIcon={<SendOutlined/>}
                                disabled={!podeEnviar({idProcessoSelecao: idProcesso, situacao, selecionados, ocupado})}
                                onClick={confirmarEnvio}>
                                Enviar
                            </ContainedButton>
                        </Grid>
                    </Grid>
                </Paper>
            </PageWrapper>
            <DialogPreviaEmail
                open={dialogState.previa}
                onClose={closeDialog('previa')}
                assunto={previa.assunto}
                corpo={previa.corpo}/>
            <DialogResultadoEnvioEmails
                open={dialogState.resultado}
                onClose={closeDialog('resultado')}
                enviados={resultado.enviados}
                falhas={resultado.falhas}/>
        </>
    );
}
